import { Divider, Form } from "antd";
import "./CampaignThreshold.css";
import PrimaryButton from "components/Buttons/PrimaryButton/PrimaryButton";
import { useDispatch, useSelector } from "react-redux";
import React, { useState, useMemo } from "react";
import centralApi from "services/centralApi";
import Spinner from "components/Spinner/Spinner";
import TextInput from "components/Inputs/TextInput/TextInput";
import { t } from "i18next";
import { setFeatureData } from "redux/features/app/featuresSettingSlice";
import { API_ENDPOINTS } from "utils/constants";
import useMessage from "hooks/useMessage";
import { getToken } from "redux/features/app/authTokenSlice";
import { CampaignChannelTypeEnum } from "utils/enums";
import Selector from "components/Selector/Selector";

const CampaignThreshold: React.FC = () => {
  const { showSuccess, showError } = useMessage();
  const dispatch: any = useDispatch();
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const thresholdConfig = useSelector(
    (state: any) => state.featuresSetting?.data[0]?.campaignThresholdConfig
  );
  
  const [formValues, setFormValues] = useState(() => ({
    campaignThresholdRange: thresholdConfig?.campaignThresholdRange || 0,
    channels: thresholdConfig?.channels || [],
  }));
  const loginUser = useSelector((state: any) => state.loginUser.data);
  const appId = useSelector((state: any) => state.activeApp.appId);
  const features = useSelector((state: any) => state.featuresSetting.data);
  const channelStatus = features[0]?.channel_status;

  // ✅ Derive available channels
  const allChannels = useMemo(() => {
    const all = Object.values(CampaignChannelTypeEnum);

    if (!channelStatus) {
      return all
        .filter((key) => key !== CampaignChannelTypeEnum.ALL)
        .map((key) => ({ value: key, label: key }));
    }

    return all
      .filter((key) => key !== CampaignChannelTypeEnum.ALL && channelStatus[key] === true)
      .map((key) => ({ value: key, label: key }));
  }, [channelStatus]);
  
  /** Submit handler */
  const handleSave = async () => {
   
    await form.validateFields();

    try {
      setLoading(true);

      const campaignThresholdConfig = {
        campaignThresholdRange: Number(formValues.campaignThresholdRange),
        channels: formValues.channels,
      };

      const authToken = await dispatch(getToken()).unwrap();

      const payload = {
        args: { campaignThresholdConfig },
        app_id: appId,
        api_key: loginUser?.api_key,
        authToken,
      };

      await centralApi("POST", API_ENDPOINTS.SAVE_APP_SETTING_URL, payload);

      dispatch(setFeatureData({ campaignThresholdConfig }));

      showSuccess(t("sucessfullySavedLabel"));
    } catch (error) {
      console.error("Error saving campaign threshold", error);
      showError(t("somethingWrongLabel"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="campaign-threshold-setting-container">
      <div className="mt-4 mb-4">
        <h6 className="campaign-threshold-setting-container-heading">
          {t("campaignThresholdSettingLabel")}
        </h6>
        <Divider className="campaign-threshold-setting-custom-divider" />

        <div className="mt-4 col-sm-8 campaign-threshold-setting-form">
          {loading ? (
            <Spinner />
          ) : (
            <Form form={form} layout="horizontal">
              {/* Threshold input */}
              <Form.Item
                label={t("campaignThresholdLabel")}
                required
              >
                <TextInput
                  type="number"
                  min={1}
                  value={formValues.campaignThresholdRange}
                  onChange={(e) =>
                    setFormValues((prev) => ({
                      ...prev,
                      campaignThresholdRange: e.target.value,
                    }))
                  }
                />
              </Form.Item>

              {/* Channel multi-select */}
              <Form.Item
                label={t("selectChannelLabel")}
                required
              >
                <Selector
                  size="middle"
                  mode="multiple"
                  options={allChannels}
                  value={formValues.channels}
                 
                  onChange={(val) =>
                    setFormValues((prev) => ({
                      ...prev,
                      channels: val,
                    }))
                  }
                />
              </Form.Item>

              <PrimaryButton
                size="middle"
                type="primary"
                onClick={handleSave}
                className="campaign-threshold-button"
              >
                {t("saveLabel")}
              </PrimaryButton>
            </Form>
          )}
        </div>
      </div>
    </div>
  );
};

export default CampaignThreshold;
